import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const avatarVariants = cva(
  "relative flex shrink-0 overflow-hidden rounded-full",
  {
    variants: {
      /**
       * Design-system size ladder. `default` (32px) intentionally carries no
       * text size so existing usages render exactly as before; pass a text-*
       * class (or a non-default size) when fallback initials need sizing.
       */
      size: {
        sm: "size-6 text-2xs",
        default: "size-8",
        lg: "size-10 text-sm",
        xl: "size-12 text-base",
      },
      /**
       * Design-system identity tints for monogram avatars. The tint paints
       * the root, so it also clears the fallback's default bg-muted.
       */
      tint: {
        rust: "bg-rust text-paper font-medium [&_[data-slot=avatar-fallback]]:bg-transparent",
        teal: "bg-teal text-paper font-medium [&_[data-slot=avatar-fallback]]:bg-transparent",
        sage: "bg-sage text-paper font-medium [&_[data-slot=avatar-fallback]]:bg-transparent",
        gold: "bg-gold text-ink font-medium [&_[data-slot=avatar-fallback]]:bg-transparent",
        plum: "bg-plum text-paper font-medium [&_[data-slot=avatar-fallback]]:bg-transparent",
        neutral:
          "bg-n-40 text-paper font-medium [&_[data-slot=avatar-fallback]]:bg-transparent",
      },
    },
    defaultVariants: { size: "default" },
  },
);

function Avatar({
  className,
  size,
  tint,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> &
  VariantProps<typeof avatarVariants>) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(avatarVariants({ size, tint, className }))}
      {...props}
    />
  );
}

function AvatarImage({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn("aspect-square size-full", className)}
      {...props}
    />
  );
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        "bg-muted flex size-full items-center justify-center rounded-full",
        className,
      )}
      {...props}
    />
  );
}

export { Avatar, AvatarFallback, AvatarImage, avatarVariants };
